import classNames from "classnames";
import { forwardRef, ImgHTMLAttributes } from "react";
import useLoadingPresentation, { LoadingPresentation } from "../../shared/hooks/useLoadingPresentation";
import { IResourceObjectUrlState, useResourceObjectUrl } from "../../shared/hooks/useResourceObjectUrl";
import "./resource-image.scss";

export interface IResourceImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** An image library `content` URL, or any other `src` — see `useResourceObjectUrl`. */
  src: string | null | undefined;
  /** Painted while the download is in flight, after it failed, or when `src` is empty. */
  fallbackSrc?: string;
}

/**
 * A drop-in `<img>` for image library content, whose `resource/{id}/file` URLs need the bearer
 * token a plain `<img src>` cannot send. The ref reaches the `<img>` itself — the splash preview
 * is a Radix `Popover.Trigger asChild` child, which anchors on the element it gets a ref to.
 *
 * The `alt` is withheld while there is nothing to paint: a browser draws a src-less `<img>` as its
 * alt text, and a thumbnail flashing its file name reads as broken. A download that outlasts the
 * kit's skeleton delay draws the sheen instead (not held once the image lands, or it would show
 * through a transparent icon); a failed one ends the wait and lets the alt text show.
 */
export const ResourceImage = forwardRef<HTMLImageElement, IResourceImageProps>(
  ({ src, fallbackSrc, alt, className, ...imgProps }: IResourceImageProps, ref): JSX.Element => {
    const resolved: IResourceObjectUrlState = useResourceObjectUrl(src);
    const painted: string | undefined = resolved.src ?? fallbackSrc;
    const isWaiting: boolean = painted === undefined && resolved.status === "loading";
    const presentation: LoadingPresentation = useLoadingPresentation({
      isLoading: isWaiting,
      hasData: false,
      minVisibleMs: 0
    });

    return (
      <img
        ref={ref}
        {...imgProps}
        className={classNames(className, { "kuika-resource-image--loading": presentation === "skeleton" })}
        src={painted}
        alt={isWaiting ? "" : alt}
        aria-busy={isWaiting || undefined}
      />
    );
  }
);

ResourceImage.displayName = "ResourceImage";
